import React from 'react';
import {
  isPdfDocument,
  leadDocumentUploadSizeError,
  MAX_LEAD_DOCUMENT_UPLOAD_BYTES,
  type LeadDocument,
} from '../../../projects/utils/leadDocumentFiles';

export type DocumentSort = { key: 'filename' | 'size' | 'created_at'; dir: 'asc' | 'desc' };

/** Dokumente eines Leads: Laden, Hoch-/Runterladen, Sortieren/Filtern/Auswählen, Vorschau, Drag&Drop. */
export function useLeadDocuments(leadId: string | number | null | undefined) {
  const [documents, setDocuments] = React.useState<LeadDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = React.useState(false);
  /** Verhindert erneutes „Lade Dokumente…“ bei gleicher Lead-ID (Tab-Wechsel, setLead mit neuem Objekt). */
  const documentsFetchedForLeadRef = React.useRef<string | null>(null);
  const [documentsUploading, setDocumentsUploading] = React.useState(false);
  const [documentsUploadProgress, setDocumentsUploadProgress] = React.useState<{
    completed: number;
    total: number;
  } | null>(null);
  const [documentsUploadError, setDocumentsUploadError] = React.useState<string | null>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [documentSearch, setDocumentSearch] = React.useState('');
  const [documentSort, setDocumentSort] = React.useState<DocumentSort>({ key: 'created_at', dir: 'desc' });
  const [selectedDocIds, setSelectedDocIds] = React.useState<Set<number>>(new Set());
  const [previewDocument, setPreviewDocument] = React.useState<LeadDocument | null>(null);
  const [previewZoom, setPreviewZoom] = React.useState(1);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const historyFileInputRef = React.useRef<HTMLInputElement>(null);
  const fabFileInputRef = React.useRef<HTMLInputElement>(null);
  const cameraInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setDocuments([]);
    documentsFetchedForLeadRef.current = null;
  }, [leadId]);

  React.useEffect(() => {
    if (!previewDocument) return;
    setPreviewZoom(1);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPreviewDocument(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [previewDocument]);

  const loadDocuments = React.useCallback(
    (options?: { force?: boolean }) => {
      const lid = leadId != null ? String(leadId) : null;
      if (!lid) return;
      const alreadyFetched = documentsFetchedForLeadRef.current === lid;
      if (alreadyFetched && !options?.force) return;
      if (!alreadyFetched) setDocumentsLoading(true);
      fetch(`/api/leads/${lid}/documents`)
        .then((res) => (res.ok ? res.json() : []))
        .then((list) => setDocuments(Array.isArray(list) ? list : []))
        .catch(() => setDocuments([]))
        .finally(() => {
          documentsFetchedForLeadRef.current = lid;
          setDocumentsLoading(false);
        });
    },
    [leadId],
  );

  const uploadFiles = React.useCallback(
    async (files: File[]): Promise<{ uploaded: number; errors: string[] }> => {
      if (!leadId || files.length === 0) return { uploaded: 0, errors: [] };
      setDocumentsUploadError(null);
      setDocumentsUploading(true);
      setDocumentsUploadProgress({ completed: 0, total: files.length });
      const uploaded: LeadDocument[] = [];
      const errors: string[] = [];
      try {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          if (file.size > MAX_LEAD_DOCUMENT_UPLOAD_BYTES) {
            errors.push(leadDocumentUploadSizeError(file.name));
            setDocumentsUploadProgress({ completed: i + 1, total: files.length });
            continue;
          }
          const form = new FormData();
          form.append('file', file);
          try {
            const res = await fetch(`/api/leads/${leadId}/documents`, {
              method: 'POST',
              body: form,
              credentials: 'include',
            });
            if (res.ok) {
              uploaded.push((await res.json()) as LeadDocument);
            } else {
              const body = (await res.json().catch(() => ({}))) as { error?: string };
              errors.push(body.error ?? `${file.name}: Upload fehlgeschlagen (${res.status})`);
            }
          } catch {
            errors.push(`${file.name}: Upload fehlgeschlagen (Netzwerkfehler)`);
          }
          setDocumentsUploadProgress({ completed: i + 1, total: files.length });
        }
        if (uploaded.length > 0) {
          setDocuments((prev) => [...uploaded, ...prev]);
        }
        if (errors.length > 0) {
          setDocumentsUploadError(errors.join(' · '));
        }
        return { uploaded: uploaded.length, errors };
      } finally {
        setDocumentsUploading(false);
        setDocumentsUploadProgress(null);
      }
    },
    [leadId],
  );

  const handleUploadDocument = React.useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files;
      if (!fileList?.length || !leadId) return;
      // FileList is live and gets cleared by resetting value — snapshot to an array first.
      const files = Array.from(fileList);
      e.target.value = '';
      await uploadFiles(files);
    },
    [leadId, uploadFiles],
  );

  const handleDrop = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const files = Array.from(e.dataTransfer.files ?? []);
      if (files.length > 0 && !documentsUploading) void uploadFiles(files);
    },
    [documentsUploading, uploadFiles],
  );

  const handleDragOver = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!documentsUploading) setIsDragging(true);
    },
    [documentsUploading],
  );

  const handleDragLeave = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const filteredAndSortedDocuments = React.useMemo(() => {
    let list = documents.filter(
      (d) => !documentSearch.trim() || d.filename.toLowerCase().includes(documentSearch.trim().toLowerCase())
    );
    list = [...list].sort((a, b) => {
      let cmp = 0;
      if (documentSort.key === 'filename') cmp = a.filename.localeCompare(b.filename);
      else if (documentSort.key === 'size') cmp = a.size - b.size;
      else cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      return documentSort.dir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [documents, documentSearch, documentSort]);

  const filteredPdfs = React.useMemo(
    () => filteredAndSortedDocuments.filter((d) => isPdfDocument(d)),
    [filteredAndSortedDocuments],
  );

  const toggleSort = React.useCallback((key: DocumentSort['key']) => {
    setDocumentSort((prev) => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
  }, []);

  const toggleSelectAll = React.useCallback(() => {
    setSelectedDocIds((prev) =>
      prev.size === filteredPdfs.length ? new Set() : new Set(filteredPdfs.map((d) => d.id)),
    );
  }, [filteredPdfs]);

  const toggleSelectDoc = React.useCallback((id: number) => {
    setSelectedDocIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleDeleteDocument = React.useCallback(
    async (docId: number) => {
      if (!leadId) return;
      const target = documents.find((d) => d.id === docId);
      if (target && target.can_delete === false) return;
      try {
        const res = await fetch(`/api/leads/${leadId}/documents/${docId}`, { method: 'DELETE' });
        if (res.ok) {
          setDocuments((prev) => prev.filter((d) => d.id !== docId));
          return;
        }
        // 409: steuerrelevantes Rechnungs-PDF — Eintrag in der Liste belassen.
        if (res.status === 409) return;
      } catch (e) {
        console.error(e);
      }
    },
    [documents, leadId],
  );

  return {
    documents,
    setDocuments,
    documentsLoading,
    documentsUploading,
    documentsUploadProgress,
    documentsUploadError,
    isDragging,
    documentSearch,
    setDocumentSearch,
    documentSort,
    selectedDocIds,
    previewDocument,
    setPreviewDocument,
    previewZoom,
    setPreviewZoom,
    fileInputRef,
    historyFileInputRef,
    fabFileInputRef,
    cameraInputRef,
    loadDocuments,
    uploadFiles,
    handleUploadDocument,
    handleDrop,
    handleDragOver,
    handleDragLeave,
    filteredAndSortedDocuments,
    filteredPdfs,
    toggleSort,
    toggleSelectAll,
    toggleSelectDoc,
    handleDeleteDocument,
  };
}

export type UseLeadDocumentsResult = ReturnType<typeof useLeadDocuments>;
